import {useEffect, useState} from 'react';
import {Prompt} from 'react-router-dom';

import classnames from 'classnames';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {SaveButton} from 'shells/button/save-button/save-button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {PageHeader} from 'shells/page-header/page-header';

import {AiFeatureType, LimitedType} from 'nutshell-graphql-types';
import type {EditionStatus} from 'nutshell-graphql-types';

import {AiFeatureGateModal} from '../../../ai-home/ai-feature-gate-modal/ai-feature-gate-modal';
import {useGetLimitsByType} from '../../../limits/use-get-limits-by-type';

import {appHistory} from '../../../../history';
import {addNotification} from '../../../../notifications/index';

import {useAcceptAiEmailTemplate} from '../../ai-design/graphql/hooks/use-accept-ai-email-template';
import {useGetAiGeneratedTemplateByEdition} from '../../ai-design/graphql/hooks/use-get-ai-generated-template-by-edition';
import {useRegenerateAiEmailTemplateFromTemplate} from '../../ai-design/graphql/hooks/use-regenerate-ai-email-template-from-template';
import type {AiDesignTargetType} from '../../ai-design/types';

import {AiDesignForm} from './ai-design-form';
import {AiGeneratingStatus} from './ai-generating-status';
import {useRegenerateAiEmailTemplate} from './graphql/hooks/use-regenerate-ai-email-template';
import {Preview} from './preview';

import styles from './ai-design-page.module.css';

type Props = {
    targetType: AiDesignTargetType;
    match: {
        params: {
            campaignId: string;
            editionId: string;
        };
    };
};

export function AiDesignPage(props: Props) {
    const {campaignId, editionId} = props.match.params;

    const [websiteUrl, setWebsiteUrl] = useState('');
    const [websiteUrlError, setWebsiteUrlError] = useState<string | null | undefined>(null);
    const [campaignDescription, setCampaignDescription] = useState('');
    const [campaignLayout, setCampaignLayout] = useState('single-column');
    const [campaignStyle, setCampaignStyle] = useState('modern');
    const [isRegenerating, setIsRegenerating] = useState(false);

    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [allowExit, setAllowExit] = useState<boolean>(false);
    const [pendingLocation, setPendingLocation] = useState<string | null | undefined>(null);

    const [isAccepting, setIsAccepting] = useState<boolean>(false);
    const [errorAccepting, setErrorAccepting] = useState<boolean>(false);
    const [selectedEditionId, setSelectedEditionId] = useState<string>(editionId);

    const {regenerateAiEmailTemplate} = useRegenerateAiEmailTemplate();
    const {regenerateAiEmailTemplateFromTemplate} = useRegenerateAiEmailTemplateFromTemplate();
    const {acceptAiEmailTemplate} = useAcceptAiEmailTemplate();
    const {aiGeneratedTemplate, editions, isAnyGenerating, refetch} =
        useGetAiGeneratedTemplateByEdition(editionId);

    const [showOutOfCreditsModal, setShowOutOfCreditsModal] = useState(false);

    const hasAiOutcomesFF = NutClientConfig.hasAiOutcomesFF;
    const {limits: outcomeLimits, isLoading: isLoadingOutcomeLimits} = useGetLimitsByType(
        LimitedType.AI_OUTCOMES
    );

    // Initialize form fields from AI campaign data
    useEffect(() => {
        if (aiGeneratedTemplate) {
            // description stores user prompt, edition emailTemplate name stores website URL
            setCampaignDescription(aiGeneratedTemplate.description || '');

            const firstEdition = editions[0];
            if (firstEdition?.emailTemplate?.name) {
                setWebsiteUrl(firstEdition.emailTemplate.name);
            }
        }
    }, [aiGeneratedTemplate, editions]);

    // Poll for status updates while any edition is generating
    useEffect(() => {
        if (!isAnyGenerating) return;

        const interval = setInterval(() => {
            refetch();
        }, 2000);

        return () => clearInterval(interval);
    }, [isAnyGenerating, refetch, isRegenerating]);

    // Auto-select first ready edition only when the currently selected edition is still generating
    useEffect(() => {
        const currentEditionIsGenerating = editions.some(
            (e) => e.id === selectedEditionId && e.status === 'GENERATING'
        );
        if (!currentEditionIsGenerating) return;

        const firstReadyEdition = editions.find((e) => e.status !== 'GENERATING');
        if (firstReadyEdition) {
            setSelectedEditionId(firstReadyEdition.id);
            window.history.replaceState(
                null,
                '',
                `/marketing/email/${props.targetType}/${campaignId}/design/${firstReadyEdition.id}`
            );
        }
    }, [editions, selectedEditionId, props.targetType, campaignId]);

    // Find the selected edition's template data for the preview
    const selectedEdition = editions.find((edition) => edition.id === selectedEditionId);

    const isLoading = isAnyGenerating || isRegenerating;
    const hasAnyReadyEdition = editions.some((e) => e.status !== 'GENERATING');
    const previewStyleName = classnames({
        [styles.preview]: hasAnyReadyEdition,
        [styles['preview-generating']]: !hasAnyReadyEdition,
    });

    const handleRegenerate = () => {
        if (hasAiOutcomesFF && outcomeLimits?.canAdd === false) {
            setShowOutOfCreditsModal(true);

            return;
        }

        if (!aiGeneratedTemplate) return;

        setIsRegenerating(true);
        setWebsiteUrlError(null);

        if (NutClientConfig.hasCampaignGeneratorV2FF) {
            regenerateAiEmailTemplateFromTemplate({
                originalCampaignId: campaignId,
                campaignId: aiGeneratedTemplate.id,
                userPrompt: campaignDescription,
                websiteUrl,
            }).catch((error) => {
                setIsRegenerating(false);
                setWebsiteUrlError(
                    safelyGetGraphQLErrorMessage(error) ||
                        'An unexpected error occurred. Please try again later.'
                );
            });
            return;
        }

        regenerateAiEmailTemplate({
            campaignId: aiGeneratedTemplate.id,
            userPrompt: campaignDescription,
            tone: getToneValue(campaignStyle),
            columns: campaignLayout === 'single-column' ? 'SINGLE_COLUMN' : 'DOUBLE_COLUMN',
            websiteUrl,
        }).catch((error) => {
            setIsRegenerating(false);
            setWebsiteUrlError(
                safelyGetGraphQLErrorMessage(error) ||
                    'An unexpected error occurred. Please try again later.'
            );
        });
    };

    useEffect(() => {
        if (!isAnyGenerating) {
            setIsRegenerating(false);
        }
    }, [isAnyGenerating]);

    useEffect(() => {
        if (allowExit && pendingLocation) {
            appHistory.replace(pendingLocation);
        }
    }, [allowExit, pendingLocation]);

    const handleAccept = () => {
        if (hasAiOutcomesFF && outcomeLimits?.canAdd === false) {
            setShowOutOfCreditsModal(true);

            return;
        }

        if (!aiGeneratedTemplate) return;

        setErrorAccepting(false);
        setIsAccepting(true);

        acceptAiEmailTemplate({
            selectedEditionId,
            targetCampaignId: campaignId,
            targetType: props.targetType,
        })
            .then(({data}) => {
                setIsAccepting(false);
                setAllowExit(true);

                if (data?.aiEmailTemplateAccept?.edition) {
                    appHistory.push(`${data.aiEmailTemplateAccept.edition.htmlUrlPath}/editor`);
                }
            })
            .catch(() => {
                setIsAccepting(false);
                setErrorAccepting(true);
                addNotification('', {
                    icon: 'frowny',
                    body: 'An unexpected error occurred. Please try again later.',
                });
            });
    };

    const backUrl = getBackUrl(props.targetType);

    // If you see this, then you likely have a regression
    // We need to manually update the cache with the new ai generated template
    if (!aiGeneratedTemplate) {
        return <LoadingPage />;
    }

    return (
        <div className='flex flex-dir-col full-height'>
            <Prompt
                when={!allowExit}
                message={(location) => {
                    setPendingLocation(location.pathname);
                    setShowConfirmationDialog(true);

                    return false;
                }}
            />
            <PageHeader
                backButton={{
                    onClick: () => appHistory.replace(backUrl),
                    text: 'Discard and exit',
                }}
                title='AI campaign designer'
                subtitle="You'll be able to edit and test this campaign before sending it."
                actionButtons={
                    <SaveButton
                        variant='primary'
                        size='big'
                        disabled={
                            !hasAnyReadyEdition || (hasAiOutcomesFF && isLoadingOutcomeLimits)
                        }
                        isSaving={isAccepting}
                        errorSaving={errorAccepting}
                        buttonText='Use and go to editor'
                        savingText='Saving…'
                        successText='Saved'
                        // @ts-expect-error
                        onClick={handleAccept}
                    />
                }
            />
            <div className={styles.container}>
                <div className={styles['preview-and-edit-container']}>
                    <AiDesignForm
                        aiCampaignId={aiGeneratedTemplate.id}
                        selectedEditionId={selectedEditionId}
                        editions={editions}
                        websiteUrl={websiteUrl}
                        websiteUrlError={websiteUrlError}
                        campaignDescription={campaignDescription}
                        campaignLayout={campaignLayout}
                        campaignStyle={campaignStyle}
                        isAnyGenerating={isAnyGenerating}
                        isRegenerating={isRegenerating}
                        isLoadingOutcomeLimits={hasAiOutcomesFF && isLoadingOutcomeLimits}
                        hasProblem={selectedEdition?.status === 'GENERATED_WITH_ISSUES'}
                        onRegenerate={handleRegenerate}
                        onWebsiteUrlChange={setWebsiteUrl}
                        onCampaignDescriptionChange={setCampaignDescription}
                        onCampaignLayoutChange={setCampaignLayout}
                        onCampaignStyleChange={setCampaignStyle}
                        onSelectEdition={(newEditionId) => {
                            setSelectedEditionId(newEditionId);
                            // Update URL without reloading the page
                            window.history.replaceState(
                                null,
                                '',
                                `/marketing/email/${props.targetType}/${campaignId}/design/${newEditionId}`
                            );
                        }}
                    />
                </div>
                <div className='full-height overflow-hidden flex flex-dir-col'>
                    {getBanner(selectedEdition?.status)}
                    <div className={previewStyleName}>
                        {isLoading && !hasAnyReadyEdition && (
                            <AiGeneratingStatus isRegenerating={isRegenerating} />
                        )}
                        {hasAnyReadyEdition && selectedEdition && (
                            <Preview
                                aiGeneratedSubject={selectedEdition.emailTemplate?.subject ?? ''}
                                aiGeneratedPreviewText={
                                    selectedEdition.emailTemplate?.previewText ?? ''
                                }
                                aiGeneratedTemplate={
                                    selectedEdition.emailTemplate?.bodyForWeb ?? ''
                                }
                            />
                        )}
                    </div>
                </div>
            </div>
            {showConfirmationDialog && (
                <ConfirmationDialog
                    title='Discard and exit?'
                    message={
                        <div className='mt-8'>
                            If you leave now, AI will stop generating your campaign and you will
                            lose your progress. Do you still want to exit?
                        </div>
                    }
                    confirmText='Leave'
                    cancelText='Cancel'
                    onCancel={() => {
                        setShowConfirmationDialog(false);
                        setPendingLocation(null);
                    }}
                    onConfirm={() => {
                        setShowConfirmationDialog(false);
                        setAllowExit(true);
                    }}
                />
            )}
            <AiFeatureGateModal
                isOpen={showOutOfCreditsModal}
                onClose={() => setShowOutOfCreditsModal(false)}
                reason='outOfCredits'
                featureName='AI campaign designer'
                aiFeatureType={AiFeatureType.EMAIL_CAMPAIGN_BUILDER}
            />
        </div>
    );
}

function getToneValue(style: string): 'MODERN' | 'CLASSIC' | 'MINIMAL' {
    if (style === 'modern') {
        return 'MODERN';
    }
    if (style === 'classic') {
        return 'CLASSIC';
    }

    return 'MINIMAL';
}

function getBanner(status: EditionStatus | undefined) {
    if (status === 'GENERATED_WITH_ISSUES') {
        return (
            <FlashBanner type='error' animate={true}>
                We had a problem generating your template - we’ve provided a sample one instead to
                get you started
            </FlashBanner>
        );
    }

    if (status === 'NEW') {
        return (
            <FlashBanner type='info' animate={true} duration={5000}>
                Your AI campaign template is ready to review and edit.
            </FlashBanner>
        );
    }

    return null;
}

function getBackUrl(targetType: AiDesignTargetType): string {
    switch (targetType) {
        case 'broadcasts':
            return '/marketing/email/broadcasts';
        case 'templates':
            return '/marketing/email/templates';
        case 'newsletters':
            return '/marketing/email/newsletters';
        case 'drip-sequences':
            return '/marketing/email/drip-sequences';
    }
}
